<template>
    <modal v-bind:show.sync="show" ref="modal" size="large">
        <div slot="header">
            <h3 class="panel-title f-s-5">{{ trans('Edit Transcription') }}</h3>
        </div>
        <div v-if="loading" class="text-center p-3">
            <div class="chartdonut chartdonut_animation--rotate-large chartdonut--large m-a">
                <svg width="53" height="53"><g><circle cx="50%" cy="50%" r="23" stroke-width="3" stroke="#02DCAA" fill="none" stroke-dashoffset="5"></circle></g></svg>
            </div>
            <p class="c-muted f-s-2 m-t-3">{{ trans('Loading transcription...') }}</p>
        </div>
        <div v-else class="row collapse split-screen-container">
            <div class="column p-r-2 split-screen-left">
                <div ref="player"></div>
            </div>
            <div class="column p-l-2 split-screen-right">
                <div v-if="transcriptionCues.length === 0" class="alert alert-info m-b-3">
                    {{ trans('No transcription data available.') }}
                </div>
                <div v-else class="transcription-editor">
                    <div
                        v-for="(cue, index) in transcriptionCues"
                        :key="index"
                        class="slat br m-t-2 m-b-2 p-2 m-r-2"
                        :class="{ 'br-c--accent': activeCueIndex === index }"
                    >
                        <div class="p-b-1">
                            <p class="c-muted f-s-1">{{ formatTime(cue.startTime) }} - {{ formatTime(cue.endTime) }}</p>
                        </div>
                        <textarea
                            v-model="cue.text"
                            v-on:focus="onCueFocus(index)"
                            class="input__field p-b-0 m-b-0 f-s-2"
                            rows="2"
                        ></textarea>
                    </div>
                </div>
            </div>
        </div>
        <div slot="footer">
            <div class="row row-collapse">
                <div class="column">
                    <button
                        type="button"
                        class="btn btn-primary m-r-3"
                        v-on:click="saveTranscription"
                        v-bind:class="{'btn-loading': saving}"
                        :disabled="saving || transcriptionCues.length === 0"
                    >
                        {{ trans('Save Changes') }}
                        <div v-if="saving" class="loader">
                            <div class="chartdonut chartdonut_animation--rotate-tiny chartdonut--tiny">
                                <svg class="i-stroke-bright" width="24" height="24"><g><circle class="icon" cx="50%" cy="50%" r="10" stroke-width="2" fill="none" stroke-dashoffset="5"></circle></g></svg>
                            </div>
                        </div>
                    </button>
                    <button type="button" class="btn btn-secondary" v-on:click="close">
                        {{ trans('Cancel') }}
                    </button>
                </div>
            </div>
        </div>
    </modal>
</template>
<script>
import Modal from './Modal.vue';
import playerjs from 'player.js';
import { transcriptionLoadUrl, transcriptionUpdateUrl } from '../../routes.js';
import { EventBus } from '../../event-bus.js';

export default {
    components: {
        Modal
    },
    props: {
        mediaId: {
            type: String,
            required: true
        },
        mediaType: {
            type: String,
            default: 'video'
        },
        // Bunny/Vimeo provider type — only meaningful for mediaType ===
        // 'video'. Passed through untouched to the 'transcription:updated'
        // event so VideoUpload.vue's Bunny-embed refresh keeps working.
        videoType: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            player: null,
            show: false,
            loading: false,
            saving: false,
            transcriptionCues: [],
            activeCueIndex: -1,
        };
    },
    watch: {
        show(value) {
            if (!value) {
                this.reset();
            } else {
                this.loadTranscription();
            }
        }
    },
    methods: {
        addEmbedCode(embedCode) {
            var node = document.createElement('div');

            this.$refs['player'].appendChild(node);

            node.className = 'embed-responsive-video';
            node.insertAdjacentHTML('beforeend', embedCode);
        },
        loadTranscription() {
            this.loading = true;

            window.axios.get(transcriptionLoadUrl(this.mediaId, this.mediaType))
                .then(response => {
                    this.loading = false;

                    if (response.data && response.data.transcription) {
                        this.parseVttContent(response.data.transcription);

                        this.$nextTick(() => {
                            if (this.mediaType === 'audio') {
                                this.initializeAudioPlayer(response.data.preview_url);
                            } else {
                                this.initializeVideoPlayer(response.data.embed);
                            }
                        });
                    } else {
                        this.$toast.danger(this.trans('Could not load transcription data.'));
                    }
                })
                .catch((error) => {
                    this.loading = false;

                    if (error.response && error.response.data.message) {
                        this.$toast.danger(error.response.data.message);
                    } else {
                        this.$toast.danger(this.trans('An error occurred while loading the transcription.'));
                    }
                });
        },
        parseVttContent(vttContent) {
            // Reset cues array
            this.transcriptionCues = [];

            // Skip the WEBVTT header line and split by cue blocks
            const cueBlocks = vttContent.split('\n\n').slice(1);

            cueBlocks.forEach(block => {
                const lines = block.trim().split('\n');

                if (lines.length >= 2) {
                    let timestampLine = 0;
                    let textLineStart = 1;

                    // Check if the first line contains a timestamp
                    if (lines[0].indexOf(' --> ') === -1) {
                        timestampLine = 1;
                        textLineStart = 2;
                    }

                    // Parse the timestamp line (format: 00:00:00.000 --> 00:00:00.000)
                    const timestamps = lines[timestampLine].split(' --> ');
                    if (timestamps.length === 2) {
                        const startTime = this.timeToSeconds(timestamps[0]);
                        const endTime = this.timeToSeconds(timestamps[1]);

                        // Join the remaining lines as the cue text
                        const text = lines.slice(textLineStart).join('\n');

                        this.transcriptionCues.push({
                            startTime,
                            endTime,
                            text
                        });
                    }
                }
            });
        },
        timeToSeconds(timeString) {
            // Parse VTT time format (00:00:00.000) to seconds
            const parts = timeString.split(':');
            let seconds = 0;

            if (parts.length === 3) {
                const [hours, minutes, secondsAndMs] = parts;
                const [secs, ms] = secondsAndMs.split('.');

                seconds = parseInt(hours) * 3600 + parseInt(minutes) * 60 + parseInt(secs);
                if (ms) {
                    seconds += parseInt(ms) / 1000;
                }
            }

            return seconds;
        },
        formatTime(seconds) {
            // Format seconds to HH:MM:SS
            const h = Math.floor(seconds / 3600);
            const m = Math.floor((seconds % 3600) / 60);
            const s = Math.floor(seconds % 60);

            return [
                h > 0 ? h.toString().padStart(2, '0') : '00',
                m.toString().padStart(2, '0'),
                s.toString().padStart(2, '0')
            ].join(':');
        },
        secondsToVttTime(seconds) {
            // Convert seconds to VTT time format (00:00:00.000)
            const h = Math.floor(seconds / 3600);
            const m = Math.floor((seconds % 3600) / 60);
            const s = Math.floor(seconds % 60);
            const ms = Math.round((seconds % 1) * 1000);

            return [
                h.toString().padStart(2, '0'),
                m.toString().padStart(2, '0'),
                s.toString().padStart(2, '0') + '.' + ms.toString().padStart(3, '0')
            ].join(':');
        },
        initializeVideoPlayer(embedCode) {
            this.addEmbedCode(embedCode)

            this.$nextTick(() => {
                this.player = new playerjs.Player(this.$refs['player'].querySelector('iframe'));
                this.player.on('ready', () => {
                    this.player.on('timeupdate', data => {
                        this.onPlayerTimeUpdate(data.seconds);
                    });
                });
            });
        },
        initializeAudioPlayer(previewUrl) {
            const audioEl = document.createElement('audio');
            audioEl.controls = true;
            audioEl.src = previewUrl;
            audioEl.style.width = '100%';

            this.audioTimeUpdateHandler = () => this.onPlayerTimeUpdate(audioEl.currentTime);
            audioEl.addEventListener('timeupdate', this.audioTimeUpdateHandler);

            this.$refs['player'].appendChild(audioEl);
            this.player = audioEl;
        },
        onPlayerTimeUpdate(currentTime) {
            // Find the cue that corresponds to the current playback time
            const index = this.transcriptionCues.findIndex(cue =>
                currentTime >= cue.startTime && currentTime < cue.endTime
            );

            if (index !== -1 && index !== this.activeCueIndex) {
                this.activeCueIndex = index;
                this.scrollToCue(index);
            }
        },
        onCueFocus(index) {
            this.activeCueIndex = index;
            this.seekToTime(this.transcriptionCues[index].startTime);
        },
        seekToTime(seconds) {
            if ( ! this.player) {
                return;
            }

            // Add a small offset to avoid showing previous and current cues
            if (this.mediaType === 'audio') {
                this.player.currentTime = seconds + 0.01;
            } else {
                this.player.setCurrentTime(seconds + 0.01);
            }
        },
        scrollToCue(index) {
            this.$nextTick(() => {
                const cueElements = document.querySelectorAll('.slat');
                if (cueElements[index]) {
                    cueElements[index].scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            });
        },
        generateVttContent() {
            // Format cues back to VTT format
            let vtt = 'WEBVTT\n\n';

            this.transcriptionCues.forEach((cue, index) => {
                const startTime = this.secondsToVttTime(cue.startTime);
                const endTime = this.secondsToVttTime(cue.endTime);
                vtt += `${startTime} --> ${endTime}\n${cue.text}\n\n`;
            });

            return vtt;
        },
        saveTranscription() {
            this.saving = true;

            window.axios.put(transcriptionUpdateUrl(this.mediaId, this.mediaType), {
                transcription: this.generateVttContent(),
            })
            .then(() => {
                this.saving = false;
                this.close();

                EventBus.$emit('transcription:updated', this.mediaId, this.videoType);
            })
            .catch((error) => {
                this.saving = false;

                if (error.response && error.response.data.message) {
                    this.$toast.danger(error.response.data.message);
                } else {
                    this.$toast.danger(this.trans('An error occurred while saving the transcription.'));
                }
            });
        },
        close() {
            this.show = false;
        },
        reset() {
            this.transcriptionCues = [];
            this.activeCueIndex = -1;

            if (this.player) {
                if (this.mediaType === 'audio') {
                    this.player.removeEventListener('timeupdate', this.audioTimeUpdateHandler);
                    this.audioTimeUpdateHandler = null;
                } else {
                    this.player.off('timeupdate');
                }

                this.player = null;
            }

            if (this.$refs.player && this.$refs.player.firstChild) {
                this.$refs.player.firstChild.remove();
            }
        }
    },
    created() {
        this.$on('close', () => {
            this.close();
        });
    },
}
</script>
<style scoped>
.split-screen-container {
    display: flex;
    width: 100%;
    height: 500px;
    max-height: 70vh;
    flex-wrap: initial;
}
.split-screen-left, .split-screen-right {
    flex: 1;
    overflow: hidden;
}
.split-screen-right {
    overflow-y: auto;
}
.transcription-editor {
    display: flex;
    flex-direction: column;
    gap: 10px;
}
</style>